import type { Transaction } from "@/apis/gql/types";
import { DateCell } from "@/components/shared/cells/date-cell";
import { RawTextCell } from "@/components/shared/cells/text";
import { BaseTable } from "@/components/shared/data-table";
import { Count } from "@/components/shared/data-table/total-count";
import { useTable } from "@/components/shared/data-table/useTable";
import { DataTableColumnHeader } from "@/components/shared/tables/ColumnHeader";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TypographyH3 } from "@/components/ui/typography";
import type { CellContext, Column, ColumnDef } from "@tanstack/react-table";
import { AlertTriangle } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AmountCell } from "../all-transactions-columns";

type ReviewStepProps = {
  onFinish: () => void;
  onBack: () => void;
  completeTransactions: Partial<Transaction>[];
  incompleteTransactions: Partial<Transaction>[];
  duplicateTransactions: Partial<Transaction>[];
  checkDuplicatesLoading: boolean;
};

enum ReviewTabs {
  COMPLETE = "COMPLETE",
  INCOMPLETE = "INCOMPLETE",
  DUPLICATES = "DUPLICATES",
}

export default function ReviewStep({
  onFinish,
  onBack,
  completeTransactions,
  incompleteTransactions,
  duplicateTransactions,
  checkDuplicatesLoading,
}: ReviewStepProps) {
  const [tab, setTab] = useState<ReviewTabs>(ReviewTabs.COMPLETE);
  const { t } = useTranslation();

  const noTransactions = completeTransactions.length === 0;

  if (checkDuplicatesLoading) return null;

  return (
    <div>
      <TypographyH3 className="mb-2">
        Überprüfe und erstelle Transaktionen
      </TypographyH3>
      <p className="text-sm text-gray-600 mb-4 font-medium">
        {t("transactions.file_import.guidance_review")}
      </p>
      <div className="flex-1 flex mt-2">
        <div className="flex flex-col min-w-0 w-full">
          <Tabs
            value={tab}
            className="flex-1 flex flex-col"
            onValueChange={(value) => setTab(value as ReviewTabs)}
          >
            <TabsList className="pb-4">
              <TabsTrigger
                value={ReviewTabs.COMPLETE}
                className="flex items-center py-3 px-6 gap-4 font-medium text-lg"
              >
                Valide
                <Count
                  count={completeTransactions.length}
                  variant={
                    completeTransactions.length > 0 ? "success" : "destructive"
                  }
                  noSuccess
                />
              </TabsTrigger>
              <TabsTrigger
                value={ReviewTabs.INCOMPLETE}
                className="flex items-center py-3 px-6 gap-2 font-medium text-lg"
              >
                Fehlerhaft
                <Count
                  count={incompleteTransactions.length}
                  variant={
                    incompleteTransactions.length > 0
                      ? "destructive"
                      : "success"
                  }
                  noSuccess
                />
              </TabsTrigger>
              <TabsTrigger
                value={ReviewTabs.DUPLICATES}
                className="flex items-center py-3 px-6 gap-2 font-medium text-lg"
              >
                Duplikate
                <Count
                  count={duplicateTransactions.length}
                  variant={
                    duplicateTransactions.length > 0 ? "destructive" : "success"
                  }
                  noSuccess
                />
              </TabsTrigger>
            </TabsList>
            <TabsContent value={ReviewTabs.COMPLETE} className="flex-1">
              <ImportTransactionsTable transactions={completeTransactions} />
            </TabsContent>
            <TabsContent value={ReviewTabs.INCOMPLETE} className="flex-1">
              <ImportTransactionsTable transactions={incompleteTransactions} />
            </TabsContent>
            <TabsContent value={ReviewTabs.DUPLICATES} className="flex-1">
              <ImportTransactionsTable transactions={duplicateTransactions} />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 flex justify-center w-full bg-white">
        <div className="max-w-4xl w-full w-full pl-8 pr-8 pb-4 gap-2">
          {noTransactions && (
            <div className="mt-4">
              <Alert variant="destructive" className="w-full mb-2">
                <AlertDescription>
                  {t("transactions.file_import.no_transactions_to_create")}
                </AlertDescription>
              </Alert>
            </div>
          )}
          <div className="flex justify-between">
            <Button variant="ghost" onClick={onBack}>
              Zurück
            </Button>
            <Button onClick={onFinish} disabled={noTransactions}>
              Transaktionen erstellen
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

type ImportTransactionsTableProps = {
  transactions: Partial<Transaction>[];
};

function ImportTransactionsTable({
  transactions,
}: ImportTransactionsTableProps) {
  const { table, clickableColumns, columnVisibility, stickyColumns, footer } =
    useTable({
      data: transactions,
      columns,
      tableName: "complete-transactions",
      footer: false,
    });

  return (
    <div className="flex flex-row flex-1 gap-4 mb-4 h-[500px]">
      <BaseTable
        table={table}
        columns={columns}
        data={transactions}
        clickableColumns={clickableColumns}
        columnVisibility={columnVisibility}
        stickyColumns={stickyColumns}
        footer={footer}
      />
    </div>
  );
}

const columns: ColumnDef<Partial<Transaction>>[] = [
  {
    size: 140,
    accessorKey: "bookingDate",
    header: ({ column }: { column: Column<Partial<Transaction>> }) => (
      <DataTableColumnHeader column={column} title="Datum" className="ml-2" />
    ),
    cell: ({ row }: CellContext<Partial<Transaction>, unknown>) => {
      if (!row.original.bookingDate) {
        return null;
      }
      return (
        <DateCell
          className="ml-2"
          value={row.original.bookingDate}
          format="dd.MM.yy"
        />
      );
    },
  },
  {
    accessorKey: "counterpartyName",
    header: ({ column }: { column: Column<Partial<Transaction>> }) => (
      <DataTableColumnHeader column={column} title="Gegenpartei" />
    ),
    cell: ({ row }: CellContext<Partial<Transaction>, unknown>) => {
      if (
        !row.original.counterpartyName ||
        row.original.counterpartyName.trim() === ""
      ) {
        return (
          <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />
        );
      }
      return <RawTextCell value={row.original.counterpartyName} />;
    },
  },
  {
    accessorKey: "purpose",
    header: ({ column }: { column: Column<Partial<Transaction>> }) => (
      <DataTableColumnHeader column={column} title="Verwendungszweck" />
    ),
    cell: ({ row }: CellContext<Partial<Transaction>, unknown>) => {
      if (!row.original.purpose) {
        return null;
      }
      return <RawTextCell value={row.original.purpose} />;
    },
  },
  {
    accessorKey: "partnerIban",
    header: ({ column }: { column: Column<Partial<Transaction>> }) => (
      <DataTableColumnHeader column={column} title="IBAN" />
    ),
    cell: ({ row }: CellContext<Partial<Transaction>, unknown>) => {
      if (!row.original.counterpartyIban) {
        return null;
      }
      return <RawTextCell value={row.original.counterpartyIban} />;
    },
  },
  {
    accessorKey: "amount",
    size: 110,
    header: ({ column }: { column: Column<Partial<Transaction>> }) => (
      <DataTableColumnHeader column={column} title="Betrag" />
    ),
    cell: ({ row }: CellContext<Partial<Transaction>, unknown>) => {
      if (
        !row.original.side ||
        !row.original.amount ||
        !row.original.currency
      ) {
        return (
          <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />
        );
      }

      return (
        <AmountCell
          className="mr-2"
          side={row.original.side}
          amount={row.original.amount}
          currency={row.original.currency}
          originalAmount={row.original.originalAmount}
          originalCurrency={row.original.originalCurrency}
        />
      );
    },
  },
];
